/**
 * ArtifactPane — the floating document pane.
 *
 * Holds one artifact at a time and renders the canonical document assembled by
 * `documentModel.buildDocument` — the same object the .docx and the clipboard
 * are serialised from, so the preview and the download cannot drift.
 *
 * The pane holds the DOCUMENT and nothing else. While one is being written it
 * shows a minimal placeholder — the live progress steps run in the chat turn,
 * beside the question that started them, which is where a conversation about
 * a document belongs.
 *
 * The citation panel docks at the bottom of the pane so a citation opens
 * beside the text it supports rather than covering it.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiCheckLight,
  PiCopySimpleLight,
  PiDownloadSimpleLight,
  PiPushPinLight,
  PiPushPinSlashLight,
} from 'react-icons/pi';
import DocumentBody from './DocumentBody';
import ResearchCitationPanel from '../../Chat/ResearchCitationPanel';
import { Artifact, artifactTypeLabel, latestArtifacts } from '../artifacts';
import { answerMetadata, buildDocument } from '../documentModel';
import { artifactFileName, artifactToDocxBlob, artifactToText, saveBlob } from '../artifactExport';
import BrandLoader from '../../shared/brand/BrandLoader';
import { CitationHighlight, ResearchCitation } from '../types';

interface Props {
  artifact: Artifact;
  /** Every artifact in the chat, for the switcher. */
  artifacts: Artifact[];
  onSelect: (turnId: string) => void;
  /** Pinning blocks the panel's close control. */
  onPinnedChange?: (pinned: boolean) => void;

  /** Docked citation, opened from inside the pane. */
  citation: {
    detail: ResearchCitation | null;
    highlight: CitationHighlight | null;
    name: string;
  } | null;
  onOpenCitation: (url: string) => void;
  onCloseCitation: () => void;
  retrievalData?: Record<string, any>;
}

const ArtifactPane: React.FC<Props> = ({
  artifact,
  artifacts,
  onSelect,
  onPinnedChange,
  citation,
  onOpenCitation,
  onCloseCitation,
  retrievalData,
}) => {
  const { t } = useTranslation();
  const [pinned, setPinned] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  /* ── export ─────────────────────────────────────────────────── */

  const handleCopy = useCallback(() => {
    if (!artifact.response) return;
    navigator.clipboard?.writeText(artifactToText(artifact.response, artifact.title));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [artifact.response, artifact.title]);

  const handleDownload = useCallback(async () => {
    if (!artifact.response || isExporting) return;
    setIsExporting(true);
    try {
      // Built client-side from the typed payload — no backend round trip.
      const blob = await artifactToDocxBlob(artifact.response, artifact.title);
      saveBlob(blob, `${artifactFileName(artifact.title)}.docx`);
    } catch {
      // Nothing downloaded; the pane stays usable and the user can retry.
    } finally {
      setIsExporting(false);
    }
  }, [artifact.response, artifact.title, isExporting]);

  // The document the pane shows and the export writes are the same object.
  const doc = useMemo(
    () => (artifact.response ? buildDocument(artifact.response, artifact.title) : null),
    [artifact.response, artifact.title]
  );
  const metadata = artifact.response ? answerMetadata(artifact.response) : null;

  // Versions are iterations of the SAME document, keyed on its identity —
  // not "every artifact that happens to share an intent".
  const versions = artifacts.filter((a) => a.documentKey === artifact.documentKey);
  // One entry per OTHER document, newest version only — versions belong
  // inside a document, not in the list of documents.
  const others = latestArtifacts(
    artifacts.filter((a) => a.documentKey !== artifact.documentKey)
  );
  const canExport = !!artifact.response;

  return (
    <div
      data-testid="artifact-pane"
      data-intent={artifact.intent}
      data-pinned={pinned}
      className="relative h-full flex flex-col min-h-0"
    >
      {/* ── Document header ──────────────────────────────────── */}
      <header className="flex-shrink-0 px-4 py-3 border-b border-hairline-soft space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-1.5 py-0 rounded-full text-[10px] font-medium
                               bg-surface-sunken border border-hairline-soft text-fg-muted">
                {t(artifactTypeLabel(artifact.intent))}
              </span>
              {artifact.isStreaming && (
                <span className="text-[10px] text-fg-muted">{t('Writing…')}</span>
              )}
            </div>
            <h2 className="mt-1 text-sm font-semibold text-fg-strong truncate"
                data-testid="artifact-title">
              {artifact.title}
            </h2>
            {/* Provenance ABOUT the answer — chrome, not document text, so it
                is translated to the UI language and never exported into a
                document written in another one. */}
            {metadata && (
              <p className="mt-0.5 text-[10px] text-fg-muted" data-testid="artifact-metadata">
                {[
                  metadata.confidence ? `${t('Confidence')}: ${metadata.confidence}` : '',
                  metadata.uncertainty,
                ].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>

          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              type="button"
              onClick={handleCopy}
              disabled={!canExport}
              data-testid="artifact-copy"
              aria-label={t('Copy document')}
              title={t('Copy document')}
              className="p-1.5 rounded-md text-fg-muted hover:text-champagne
                         disabled:opacity-40 transition-colors duration-200 ease-slide"
            >
              {copied
                ? <PiCheckLight className="w-4 h-4 text-champagne" />
                : <PiCopySimpleLight className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={!canExport || isExporting}
              data-testid="artifact-download"
              aria-label={t('Download as Word')}
              title={t('Download as Word')}
              className="p-1.5 rounded-md text-fg-muted hover:text-champagne
                         disabled:opacity-40 transition-colors duration-200 ease-slide"
            >
              <PiDownloadSimpleLight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setPinned((p) => { onPinnedChange?.(!p); return !p; })}
              data-testid="artifact-pin"
              aria-label={pinned ? t('Unpin panel') : t('Pin panel')}
              title={pinned ? t('Unpin panel') : t('Pin panel')}
              className={clsx(
                'p-1.5 rounded-md transition-colors duration-200 ease-slide',
                pinned ? 'text-champagne' : 'text-fg-muted hover:text-fg-strong'
              )}
            >
              {pinned
                ? <PiPushPinSlashLight className="w-4 h-4" />
                : <PiPushPinLight className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Versions of THIS artifact — successive turns of the same shape. */}
        {versions.length > 1 && (
          <div className="flex items-center gap-1 flex-wrap" data-testid="artifact-versions">
            <span className="text-[10px] text-fg-muted">{t('Version')}</span>
            {versions.map((version) => (
              <button
                key={version.turnId}
                type="button"
                onClick={() => onSelect(version.turnId)}
                data-testid="artifact-version-chip"
                data-active={version.turnId === artifact.turnId}
                className={clsx(
                  'px-1.5 py-0 rounded-full text-[10px] font-medium border',
                  'transition-colors duration-200 ease-slide',
                  version.turnId === artifact.turnId
                    ? 'bg-champagne/15 border-champagne/40 text-fg-strong'
                    : 'bg-surface-sunken border-hairline-soft text-fg-muted hover:text-fg-strong'
                )}
              >
                v{version.version}
              </button>
            ))}
          </div>
        )}

        {/* Other artifacts in this chat. */}
        {others.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap" data-testid="artifact-switcher">
            {others.map((other) => (
              <button
                key={other.turnId}
                type="button"
                onClick={() => onSelect(other.turnId)}
                data-testid="artifact-switch"
                title={other.title}
                className="px-2 py-0.5 rounded-full text-[10px] font-medium max-w-[9rem] truncate
                           bg-surface-sunken border border-hairline-soft text-fg-muted
                           hover:text-fg-strong transition-colors duration-200 ease-slide"
              >
                {other.title}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* ── Document body ────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 py-4" data-testid="artifact-body">
        {artifact.isStreaming && !doc ? (
          // Deliberately minimal. The live steps run in the chat turn, where
          // the conversation is; the pane is for the document itself, so
          // until there is one it says only that one is coming.
          <div className="h-full flex flex-col items-center justify-center gap-3"
               data-testid="artifact-drafting">
            <BrandLoader size="sm" />
            <p className="text-xs text-fg-muted">{t('Writing the document…')}</p>
          </div>
        ) : doc ? (
          // ONE assembly, shared with the export — see `documentModel`.
          <DocumentBody
            doc={doc}
            citations={artifact.response?.citations || []}
            onOpenCitation={onOpenCitation}
          />
        ) : (
          // Reopened from history and the blob has not landed yet.
          <p className="text-xs text-fg-muted" data-testid="artifact-loading">
            {t('Loading document…')}
          </p>
        )}
      </div>

      {/* ── Docked citations ─────────────────────────────────── */}
      {citation && (
        <div
          className="flex-shrink-0 h-1/2 border-t border-hairline"
          data-testid="artifact-citation-dock"
        >
          <ResearchCitationPanel
            citationName={citation.name}
            citation={citation.detail}
            highlight={citation.highlight}
            retrievalData={retrievalData}
            onClose={onCloseCitation}
          />
        </div>
      )}
    </div>
  );
};

export default ArtifactPane;
